/**
 * Structured logger. Thin wrapper over Pino.
 *
 * Contract: .tondocs/infra/LOGGING.md — read it before adding log calls.
 * Rule of thumb: log events (state changed / error / anomaly), not status.
 * Silent success is the default for repeated no-op branches.
 */

import pino from 'pino'

export const logger = pino({
  level: process.env.LOG_LEVEL ?? 'info',

  // No pid / hostname — useless on Vercel serverless and just inflates every line.
  base: null,

  timestamp: pino.stdTimeFunctions.isoTime,

  // Defence-in-depth. Primary rule is "don't pass PII/secrets in", but paths
  // below catch common accidents. Add new ones here when new shapes appear.
  redact: {
    paths: [
      'authorization',
      'req.headers.authorization',
      'req.headers["paypal-transmission-sig"]',
      'req.headers["paypal-cert-url"]',
      'headers.authorization',
      'headers["paypal-transmission-sig"]',
      'headers["paypal-cert-url"]',
      'payment_source',
      '*.payment_source',
      // Common PII leak shapes — field names, not whole payloads
      '*.email_address',
      '*.phone_number',
      'payer.email_address',
      'payer.phone',
    ],
    censor: '[REDACTED]',
  },
})

/**
 * Create a logger bound to a `component`. Every line emitted from it will
 * carry the `component` field, so aggregator queries can scope by source.
 *
 * Naming: dot-separated, e.g. `webhook.paypal`, `reconcile.section4`,
 * `paypal.client`. See LOGGING.md §4.
 */
export const childLogger = (component: string) => logger.child({ component })
